import React, { useEffect, useState, useRef, useId, useLayoutEffect } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { openPhoebeChat } from './GlobalPhoebeChat';
type InsightBriefing = {
  metrics: {
    label: string;
    value: string;
  }[];
  signals: string[];
  actions: string[];
  actionResponses?: Record<string, string>;
};
const INSIGHT_BRIEFINGS: Record<number, InsightBriefing> = {
  6: {
    metrics: [{
      label: 'Monthly participation',
      value: '80%'
    }, {
      label: 'Change',
      value: '+20% vs 30 days ago'
    }, {
      label: 'Learners active this month',
      value: '198 of 248'
    }],
    signals: ['Monthly participation has reached 80% of learners.', 'Learning activity is up 20% compared with the prior 30-day period.', 'A timely reminder can help convert active learners into completed modules.'],
    actions: ['Send a completion reminder to active learners', 'Identify learners close to completing a module', 'Review active learner progress by skill']
  },
  7: {
    metrics: [{
      label: 'Completed all modules',
      value: '10%'
    }, {
      label: 'Change',
      value: '-20% vs 30 days ago'
    }, {
      label: 'Learners needing follow-up',
      value: '223 of 248'
    }],
    signals: ['Only 10% of learners have completed every skill module.', 'Completion fell 20% from the prior 30-day period.', 'Learners with one or two modules remaining are the strongest follow-up opportunity.'],
    actions: ['Send a targeted completion reminder', 'Identify learners with one or two modules remaining', 'Schedule a completion support check-in']
  },
  8: {
    metrics: [{
      label: 'Misinformation rate',
      value: '22%'
    }, {
      label: 'Support focus',
      value: 'Flexbox, Grid, positioning'
    }, {
      label: 'Priority',
      value: 'Above other core modules'
    }],
    signals: ['CSS Fundamentals has a 22% misinformation rate, above the other core modules.', 'Flexbox, Grid, and positioning need clearer guided practice.', 'Strengthening these concepts before the next assessment can improve learner readiness.'],
    actions: ['Share targeted CSS practice resources', 'Run a Flexbox and Grid support session', 'Review positioning questions for clarity']
  },
  9: {
    metrics: [{
      label: 'Average first-attempt score',
      value: '64.93%'
    }, {
      label: 'Pass threshold',
      value: '70%'
    }, {
      label: 'Readiness gap',
      value: '5.07 percentage points'
    }],
    signals: ['Average first-attempt score is 64.93%, below the 70% pass threshold.', 'Confident incorrect responses point to concepts that need intervention before another full assessment.', 'Targeted pre-assessment practice can strengthen readiness and reduce unnecessary retakes.'],
    actions: ['Identify concepts driving confident incorrect answers', 'Share a targeted pre-assessment review', 'Offer a short readiness check before retakes']
  }
};
export interface AIInsightPopoverProps {
  headline: string;
  insightBeforeMetric: string;
  highlightedMetric: string;
  insightAfterMetric?: string;
  explanation: string;
  insightId?: number;
  briefing?: InsightBriefing;
  align?: 'left' | 'right';
}
export function AIInsightPopover({
  headline,
  insightBeforeMetric,
  highlightedMetric,
  insightAfterMetric = '',
  explanation,
  insightId,
  briefing: customBriefing,
  align = 'right'
}: AIInsightPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null);
  const suppressFocusOpenRef = useRef(false);
  const skipClickRef = useRef(false);
  const popoverId = useId();
  const dismiss = (returnFocus = false) => {
    setIsOpen(false);
    setIsPinned(false);
    if (returnFocus) {
      suppressFocusOpenRef.current = true;
      triggerRef.current?.focus();
      window.setTimeout(() => {
        suppressFocusOpenRef.current = false;
      }, 0);
    }
  };
  useLayoutEffect(() => {
    if (!isOpen) return;
    const positionPopover = () => {
      const rect = popoverRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 16;
      const leftOverflow = viewportPadding - rect.left;
      const rightOverflow = rect.right - (window.innerWidth - viewportPadding);
      setHorizontalOffset(leftOverflow > 0 ? leftOverflow : rightOverflow > 0 ? -rightOverflow : 0);
    };
    positionPopover();
    window.addEventListener('resize', positionPopover);
    window.addEventListener('scroll', positionPopover, true);
    return () => {
      window.removeEventListener('resize', positionPopover);
      window.removeEventListener('scroll', positionPopover, true);
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!isPinned && !rootRef.current?.contains(event.target as Node)) {
        dismiss();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss(true);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isPinned]);
  const closeWhenFocusLeaves = () => {
    window.requestAnimationFrame(() => {
      if (!isPinned && !rootRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    });
  };
  const handleTriggerPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    suppressFocusOpenRef.current = true;
    window.setTimeout(() => {
      suppressFocusOpenRef.current = false;
    }, 0);
    if (event.pointerType !== 'mouse') {
      skipClickRef.current = true;
      setIsOpen(open => !open);
      setIsPinned(pinned => !pinned);
    }
  };
  const togglePinnedPopover = () => {
    setIsPinned(pinned => !pinned);
    setIsOpen(true);
  };
  return <div ref={rootRef} className="relative inline-flex" onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => {
    if (!isPinned) setIsOpen(false);
  }} onBlurCapture={closeWhenFocusLeaves} data-id="element-231" data-mp="3d6zzv056mfes">
      <button ref={triggerRef} type="button" aria-label={`${isPinned ? 'Unpin' : 'Pin'} AI insight: ${headline}`} aria-expanded={isOpen} aria-pressed={isPinned} aria-controls={popoverId} onPointerDown={handleTriggerPointerDown} onFocus={() => {
      if (!suppressFocusOpenRef.current) setIsOpen(true);
    }} onClick={() => {
      if (skipClickRef.current) {
        skipClickRef.current = false;
        return;
      }
      togglePinnedPopover();
    }} className={`inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-[#30718D] shadow-sm transition-colors hover:bg-[#30718D]/10 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${isPinned ? 'border-[#30718D] bg-[#30718D]/10' : 'border-[#30718D]/30'}`} data-id="element-232" data-mp="3qywouvbk096y">
        <Sparkles size={15} aria-hidden="true" data-id="element-233" data-mp="sp4hyx96ztqi" />
      </button>

      <AnimatePresence data-id="element-234" data-mp="nv9q36jfo8gc">
        {isOpen && <motion.section ref={popoverRef} id={popoverId} role="dialog" aria-label="AI insight" initial={{
        opacity: 0,
        x: horizontalOffset,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        x: horizontalOffset,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: horizontalOffset,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.16,
        ease: 'easeOut'
      }} className={`absolute top-full z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white p-4 shadow-xl ${align === 'right' ? 'right-0' : 'left-0'}`} data-id="element-235" data-mp="ntyn38xtb4qa">
            <div className="mb-3 flex items-center gap-2 text-[#30718D]" data-id="element-236" data-mp="2iyqutwgg07nl">
              <Sparkles size={16} aria-hidden="true" data-id="element-237" data-mp="241gadxltnrfq" />
              <p className="text-xs font-semibold uppercase tracking-wide" data-id="element-238" data-mp="291cedwuiriva">
                AI insights
              </p>
            </div>
            <h3 className="text-sm font-bold text-gray-900" data-id="element-239" data-mp="sm1d0sppaxk3">{headline}</h3>
            <p className="mt-1.5 text-sm font-medium leading-snug text-gray-800" data-id="element-240" data-mp="xmp4ffhudgeu">
              {insightBeforeMetric}{' '}
              <span className="text-[#007558]" data-id="element-241" data-mp="3bsvmx1pzughf">{highlightedMetric}</span>
              {insightAfterMetric}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-gray-600" data-id="element-242" data-mp="36uluuzzkfw01">
              {explanation}
            </p>
            <button type="button" onClick={() => {
          const briefing = customBriefing || (insightId ? INSIGHT_BRIEFINGS[insightId] : undefined);
          const metrics = briefing?.metrics || [{
            label: 'Key metric',
            value: highlightedMetric
          }];
          const signals = briefing?.signals || [explanation];
          const actions = briefing?.actions || ['Help me plan the next step'];
          dismiss();
          openPhoebeChat({
            prompt: `Tell me more about this insight: ${headline}`,
            response: buildInsightResponse(headline, metrics, signals, actions),
            suggestedQuestions: actions,
            suggestionResponses: Object.fromEntries(actions.map(action => [action, briefing?.actionResponses?.[action] || buildActionResponse(action, headline, signals)]))
          });
        }} className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 transition-colors hover:border-[#30718D] hover:bg-[#30718D]/5 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-243" data-mp="3ksipvrydfucl">
              Learn more with Phoebe <ArrowRight size={16} aria-hidden="true" data-id="element-244" data-mp="37vpnifr3tk53" />
            </button>
          </motion.section>}
      </AnimatePresence>
    </div>;
}
function buildInsightResponse(headline: string, metrics: {
  label: string;
  value: string;
}[], signals: string[], actions: string[]) {
  return `**${headline}**\n\n**Snapshot**\n${metrics.map(metric => `• ${metric.label}: ${metric.value}`).join('\n')}\n\n**What the data shows**\n${signals.map(signal => `• ${signal}`).join('\n')}\n\n**Suggested actions**\n${actions.map(action => `• ${action}`).join('\n')}\n\nChoose an action below and I’ll help you turn it into a practical next step.`;
}
function buildActionResponse(action: string, headline: string, signals: string[]) {
  return `Here’s a focused way to move forward with “${action}.”\n\n**Why this matters**\n${headline}\n\n**Recommended approach**\n1. Start with this signal: ${signals[0]}\n2. Complete one focused follow-up this week.\n3. Review the relevant outcome in 7 days and adjust if needed.\n\nWould you like help drafting the outreach, identifying the right learners, or defining a success measure?`;
}